import type { Rng } from '../../core/rng';
import type { Vec3 } from '../../geo/types';
import type { RoofProfile } from '../fabric/roofProfile';

export const HEARTH_PUFFS = 20;

export interface Hearth {
  readonly at: Vec3;
  readonly phase: number;
  readonly lifetime: number;
  readonly rise: number;
}

export function planHearthSmoke(roofs: readonly RoofProfile[], rng: Rng): readonly Hearth[] {
  const hearths: Hearth[] = [];
  for (const at of rng.shuffle(roofs.flatMap((r) => r.chimneys))) {
    if (hearths.some((h) => Math.hypot(h.at[0] - at[0], h.at[2] - at[2]) < 16)) continue;
    const lifetime = rng.range(8, 11);
    hearths.push({ at, lifetime, phase: rng.range(0, lifetime), rise: rng.range(0.4, 0.58) });
    if (hearths.length === 320) break;
  }
  return hearths;
}

/** Successive soft-edged painted puffs are transparent before they return to their flue. */
export function hearthSmokePoseAt(hearth: Hearth, puff: number, time: number, visibility: number) {
  const elapsed = time + hearth.phase + (puff * hearth.lifetime) / HEARTH_PUFFS;
  const age = (((elapsed % hearth.lifetime) + hearth.lifetime) % hearth.lifetime) / hearth.lifetime;
  const seconds = age * hearth.lifetime;
  const gust = 0.8 + 0.2 * Math.sin((time - seconds) * 0.47);
  const drift = seconds * age * gust;
  const radius = 0.14 + age * 1.3;
  /* The painter pass averages sparse coverage away; keep the middle of the column visible,
     then fade its tail completely before recycling it into the chimney mouth. */
  const opacity =
    0.38 *
    Math.min(1, age * 12) *
    Math.min(1, ((1 - age) / 0.4) ** 3) *
    Math.max(0, Math.min(1, visibility));
  return {
    age,
    radius,
    opacity,
    position: [
      hearth.at[0] + drift * 0.48,
      hearth.at[1] + seconds * hearth.rise + radius * 0.6 + 0.025,
      hearth.at[2] + drift * 0.22,
    ] as Vec3,
  };
}
